import { ref, toValue, watch, type MaybeRefOrGetter } from 'vue'
import usePut from './api/usePut'
import { ApiRequestStatus } from '../models/api/api-request-status'
import { useSnackStore } from '../../stores/snack.store'
import { useI18n } from 'vue-i18n'
import type { MaybeRef, Mutable } from '@vueuse/core'
import useDelete from './api/useDelete'
import usePost from './api/usePost'
import { useDialog } from '../components/base/dialog/use-dialog'
import type { Objective, ObjectiveAction } from './use-objectives'
import { useApiClient } from './api/useApiClient'
import { FetchMethod } from '../models/api/fetch-method'
import { useEventBus } from '@vueuse/core'
import { EventKey } from '../events/event-key'
import { useCoverObjective } from './use-cover-objective'

export interface UseObjectiveOptions {
  /** When set and autoCover is true, decisive actions also tick the dialog checklist. */
  dialogId?: MaybeRefOrGetter<string | undefined>
  autoCover?: MaybeRefOrGetter<boolean>
}

export const useObjective = (
  initialValue: MaybeRef<Objective>,
  options: UseObjectiveOptions = {},
) => {
  const { t } = useI18n()
  const snackStore = useSnackStore()
  const objectiveUpdatedBus = useEventBus(EventKey.objectiveUpdated)
  const objectiveId = toValue(initialValue).id
  const { cover } = useCoverObjective(() => toValue(options.dialogId) ?? '')

  const shouldAutoCover = () => {
    if (!toValue(options.autoCover)) return false
    return !!toValue(options.dialogId)
  }

  const coverIfNeeded = async () => {
    if (!shouldAutoCover()) return
    await cover(objectiveId)
  }

  const { mutate: updateObjective, status: statusUpdateObjective } = usePut<Partial<Objective>>(
    `objectives/${objectiveId}`,
  )
  const { remove: removeObjective, status: statusRemoveObjective } = useDelete<string>(
    `objectives/${objectiveId}`,
  )
  const { mutate: achieveMutate, status: achieveStatus } = usePut(
    `objectives/${objectiveId}/achieve`,
  )
  const { mutate: reopenMutate, status: reopenStatus } = usePut(`objectives/${objectiveId}/reopen`)
  const { mutate: archiveMutate, status: archiveStatus } = usePut(
    `objectives/${objectiveId}/archive`,
  )
  const { mutate: restoreMutate, status: restoreStatus } = usePut(
    `objectives/${objectiveId}/restore`,
  )
  const { mutate: promoteMutate, status: promoteStatus } = usePut(
    `objectives/${objectiveId}/promote`,
  )
  const {
    mutate: createAction,
    status: createActionStatus,
    data: createdAction,
  } = usePost<string | { id: string }, { title: string; position?: number }>(
    `objectives/${objectiveId}/actions`,
  )
  const { mutate: orderActions, status: orderActionsStatus } = usePut<
    unknown,
    { actionIds: string[] }
  >(`objectives/${objectiveId}/actions/order`)
  const { execute: executeActionRequest, status: actionRequestStatus } = useApiClient()

  const isEditing = ref(false)
  const newActionTitle = ref('')
  const editableObjective = ref<Mutable<Objective>>({
    ...toValue(initialValue),
    actions: [...(toValue(initialValue).actions ?? [])],
  })

  watch(
    () => toValue(initialValue),
    (current) => {
      editableObjective.value = {
        ...current,
        actions: [...(current.actions ?? [])],
      }
    },
  )

  const notifyUpdated = () => {
    objectiveUpdatedBus.emit()
  }

  const cancelEdit = () => {
    isEditing.value = false
    const current = toValue(initialValue)
    editableObjective.value.title = current.title
    editableObjective.value.description = current.description
    editableObjective.value.feedbackTopicId = current.feedbackTopicId
  }

  const editObjective = () => {
    isEditing.value = true
  }

  const saveObjective = async () => {
    await updateObjective({
      id: editableObjective.value.id,
      title: editableObjective.value.title,
      description: editableObjective.value.description,
      feedbackTopicId: editableObjective.value.feedbackTopicId ?? null,
    })

    if (statusUpdateObjective.value == ApiRequestStatus.Success) {
      isEditing.value = false
      await coverIfNeeded()
      notifyUpdated()
      snackStore.showInfo(t('objective.update.success'))
    }
    if (statusUpdateObjective.value == ApiRequestStatus.Error) {
      notifyUpdated()
      snackStore.showError(t('objective.update.error'))
    }
  }

  const {
    isOpen: isOpenDeleteObjectiveDialog,
    openDialog: openDeleteObjectiveDialog,
    closeDialog: closeDeleteObjectiveDialog,
  } = useDialog()

  const {
    isOpen: isOpenToggleImpulseDialog,
    openDialog: openToggleImpulseDialog,
    closeDialog: closeToggleImpulseDialog,
  } = useDialog()

  const toggleIsImpulse = async () => {
    await promoteMutate({})
    closeToggleImpulseDialog()

    if (promoteStatus.value == ApiRequestStatus.Success) {
      await coverIfNeeded()
      notifyUpdated()
      snackStore.showInfo(t('objective.update.success'))
      return
    }
    notifyUpdated()
    snackStore.showError(t('objective.update.error'))
  }

  const deleteObjective = async () => {
    await removeObjective({})

    closeDeleteObjectiveDialog()

    if (statusRemoveObjective.value == ApiRequestStatus.Success) {
      snackStore.showInfo(t('objective.delete.success'))
      notifyUpdated()
    }
    if (statusRemoveObjective.value == ApiRequestStatus.Error) {
      notifyUpdated()
      snackStore.showError(t('objective.delete.error'))
    }
  }

  const runLifecycle = async (
    mutate: (body: unknown) => Promise<void>,
    status: { value: ApiRequestStatus },
    successKey: string,
    errorKey: string,
    coverOnSuccess = false,
  ) => {
    await mutate({})
    if (status.value == ApiRequestStatus.Success) {
      if (coverOnSuccess) {
        await coverIfNeeded()
      }
      notifyUpdated()
      snackStore.showInfo(t(successKey))
      return true
    }
    notifyUpdated()
    snackStore.showError(t(errorKey))
    return false
  }

  const achieveObjective = async () => {
    await runLifecycle(
      achieveMutate,
      achieveStatus,
      'objective.achieve.success',
      'objective.achieve.error',
      true,
    )
  }

  const reopenObjective = async () => {
    await runLifecycle(
      reopenMutate,
      reopenStatus,
      'objective.reopen.success',
      'objective.reopen.error',
    )
  }

  const restoreObjective = async () => {
    await runLifecycle(
      restoreMutate,
      restoreStatus,
      'objective.restore.success',
      'objective.restore.error',
    )
  }

  const archiveObjective = async () => {
    await archiveMutate({})
    if (archiveStatus.value == ApiRequestStatus.Success) {
      await coverIfNeeded()
      notifyUpdated()
      snackStore.showInfo(t('objective.archive.success'), {
        label: t('objective.restore.action'),
        callback: () => {
          void restoreObjective()
        },
      })
      return
    }
    notifyUpdated()
    snackStore.showError(t('objective.archive.error'))
  }

  const createdActionId = (payload: string | { id: string } | undefined) => {
    if (typeof payload === 'string' && payload.length > 0) {
      return payload
    }
    if (payload && typeof payload === 'object' && typeof payload.id === 'string') {
      return payload.id
    }
  }

  const addAction = async () => {
    const position = editableObjective.value.actions.length
    await createAction({ title: '', position })
    if (createActionStatus.value != ApiRequestStatus.Success) {
      notifyUpdated()
      snackStore.showError(t('objective.action.error'))
      return
    }

    const id = createdActionId(createdAction.value)
    if (id) {
      editableObjective.value = {
        ...editableObjective.value,
        actions: [
          ...editableObjective.value.actions,
          { id, title: '', isChecked: false, position },
        ],
      }
    }
    notifyUpdated()
    return id
  }

  const renameAction = async (action: ObjectiveAction, title: string) => {
    await executeActionRequest({
      method: FetchMethod.PUT,
      endpoint: `objectives/actions/${action.id}`,
      omitOrganization: false,
      body: { title },
    })
    if (actionRequestStatus.value == ApiRequestStatus.Success) {
      notifyUpdated()
      return
    }
    notifyUpdated()
    snackStore.showError(t('objective.action.error'))
  }

  const setActionChecked = async (action: ObjectiveAction, isChecked: boolean) => {
    await executeActionRequest({
      method: FetchMethod.PUT,
      endpoint: `objectives/actions/${action.id}/checked`,
      omitOrganization: false,
      body: { isChecked },
    })
    if (actionRequestStatus.value == ApiRequestStatus.Success) {
      notifyUpdated()
      return
    }
    notifyUpdated()
    snackStore.showError(t('objective.action.error'))
  }

  const deleteAction = async (action: ObjectiveAction) => {
    await executeActionRequest({
      method: FetchMethod.DELETE,
      endpoint: `objectives/actions/${action.id}`,
      omitOrganization: false,
      body: {},
    })
    if (actionRequestStatus.value == ApiRequestStatus.Success) {
      notifyUpdated()
      return
    }
    notifyUpdated()
    snackStore.showError(t('objective.action.error'))
  }

  const reorderActions = async (actionIds: string[]) => {
    const previousActions = [...editableObjective.value.actions]
    editableObjective.value = {
      ...editableObjective.value,
      actions: actionIds.flatMap((id, position) => {
        const action = previousActions.find((item) => item.id === id)
        return action ? [{ ...action, position }] : []
      }),
    }
    await orderActions({ actionIds })
    if (orderActionsStatus.value == ApiRequestStatus.Success) {
      notifyUpdated()
      return
    }
    notifyUpdated()
    editableObjective.value = {
      ...editableObjective.value,
      actions: previousActions,
    }
    snackStore.showError(t('objective.action.error'))
  }

  return {
    isEditing,
    editableObjective,
    cancelEdit,
    saveObjective,
    editObjective,
    deleteObjective,
    isOpenDeleteObjectiveDialog,
    openDeleteObjectiveDialog,
    closeDeleteObjectiveDialog,
    isOpenToggleImpulseDialog,
    openToggleImpulseDialog,
    closeToggleImpulseDialog,
    toggleIsImpulse,
    achieveObjective,
    reopenObjective,
    archiveObjective,
    restoreObjective,
    newActionTitle,
    addAction,
    renameAction,
    setActionChecked,
    deleteAction,
    reorderActions,
    coverIfNeeded,
  }
}
